import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ReadyState } from 'react-use-websocket'

import {
  DxTradeAccountMetrics,
  DxTradeAccountPortfolioWsMessage,
  DxTradePortfolio,
} from '@model/DxTrade.types'

import { useWebSocket } from '@hooks/useWebSocket'

import { DXTRADE_POSITION_EFFECT } from '../../utils/constants'

const DXTRADE_ACCOUNT_WS_PATH = 'platform'

export type DxtradeAccountMetricsMap = Record<string, DxTradeAccountMetrics>

// Module-level store — accessible outside React context (e.g. from modals rendered in portals)
function createModuleStore<T>() {
  const store = new Map<string, T>()
  const listeners = new Map<string, Set<() => void>>()

  function getListeners(key: string): Set<() => void> {
    let set = listeners.get(key)
    if (!set) {
      set = new Set()
      listeners.set(key, set)
    }
    return set
  }

  return {
    update(key: string, value: T) {
      store.set(key, value)
      getListeners(key).forEach((l) => l())
    },
    subscribe(key: string, listener: () => void): () => void {
      const set = getListeners(key)
      set.add(listener)
      return () => set.delete(listener)
    },
    getSnapshot(key: string): T | undefined {
      return store.get(key)
    },
  }
}

const metricsStore = createModuleStore<DxTradeAccountMetrics>()
const portfolioStore = createModuleStore<DxTradePortfolio>()

export function updateMetricsStore(metrics: DxTradeAccountMetrics[]): void {
  for (const m of metrics) {
    if (m.account) {
      metricsStore.update(m.account, m)
    }
  }
}

export const subscribeToMetricsStore = metricsStore.subscribe
export const getMetricsStoreSnapshot = metricsStore.getSnapshot

export const subscribeToPortfolioStore = portfolioStore.subscribe
export const getPortfolioStoreSnapshot = portfolioStore.getSnapshot

const prevPortfolioSnapshots = new Map<string, string>()

export type UseDxtradeAccountWebSocketResult = {
  readyState: ReadyState
  metricsMap: DxtradeAccountMetricsMap
}

export function useDxtradeAccountWebSocket(): UseDxtradeAccountWebSocketResult {
  const [metricsMap, setMetricsMap] = useState<DxtradeAccountMetricsMap>({})
  const readyStateRef = useRef<ReadyState>(ReadyState.CLOSED)
  const sendMessageRef = useRef<(message: string) => void>(() => {})
  const queryClient = useQueryClient()

  const markOrdersHistoryStale = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['trade', 'dxtrade', 'ordersHistory'] })
  }, [queryClient])

  const { sendMessage, readyState } = useWebSocket(DXTRADE_ACCOUNT_WS_PATH, {
    share: true,
    heartbeat: false,
    onMessage: (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        if (data.$type === 'AccountMetrics' && Array.isArray(data.Metrics)) {
          updateMetricsStore(data.Metrics as DxTradeAccountMetrics[])
          setMetricsMap((prev) => {
            const next = { ...prev }
            for (const m of data.Metrics as DxTradeAccountMetrics[]) {
              if (m.account) next[m.account] = m
            }
            return next
          })
        } else if (data.$type === 'AccountPortfolio' && Array.isArray(data.Portfolios)) {
          const msg = data as DxTradeAccountPortfolioWsMessage
          for (const portfolio of msg.Portfolios) {
            const storeKey = portfolio.account.includes(':')
              ? portfolio.account.split(':')[1]
              : portfolio.account

            const closedByOrderLeg = new Set<string>()
            for (const order of portfolio.orders ?? []) {
              for (const leg of order.legs ?? []) {
                if (leg.positionEffect === DXTRADE_POSITION_EFFECT.CLOSE && leg.positionCode) {
                  closedByOrderLeg.add(leg.positionCode)
                }
              }
            }

            const openPositions = portfolio.positions.filter(
              (p) => p.quantity > 0 || !(p.positionCode && closedByOrderLeg.has(p.positionCode))
            )

            // Detect opens, closes, and partial closes by comparing position snapshots - for updating history tab
            const snapshot = openPositions
              .map((position) => `${position.positionCode}:${position.quantity}`)
              .sort()
              .join(',')
            const prevSnapshot = prevPortfolioSnapshots.get(storeKey)
            if (prevSnapshot !== undefined && prevSnapshot !== snapshot) {
              markOrdersHistoryStale()
            }
            prevPortfolioSnapshots.set(storeKey, snapshot)

            portfolioStore.update(storeKey, { ...portfolio, positions: openPositions })
          }
        }
      } catch (e) {
        console.error('[DxtradeAccountWebSocket] Failed to parse message:', e)
      }
    },
  })

  useEffect(() => {
    readyStateRef.current = readyState
    sendMessageRef.current = sendMessage
  }, [readyState, sendMessage])

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) return

    sendMessage(JSON.stringify({ $type: 'SubscribeAccountMetrics' }))
    sendMessage(JSON.stringify({ $type: 'SubscribeAccountPortfolio' }))
  }, [readyState, sendMessage])

  useEffect(() => {
    return () => {
      if (readyStateRef.current === ReadyState.OPEN) {
        sendMessageRef.current(JSON.stringify({ $type: 'UnsubscribeAccountMetrics' }))
        sendMessageRef.current(JSON.stringify({ $type: 'UnsubscribeAccountPortfolio' }))
      }
    }
  }, [])

  return { readyState, metricsMap }
}
